if (!customElements.get('fabric-selector')) {
  class FabricSelector extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.stainHandles = ['stains', 'stain', 'stain-type'];
      this.propertyInput = this.querySelector('[data-fabric-property]');
      this.nameEl = this.querySelector('[data-fabric-name]');
      this.typeEl = this.querySelector('[data-fabric-type]');
      this.priceEl = this.querySelector('[data-fabric-price]');

      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) this.select(e.target);
      });

      // Pre-warm the hero render for whichever swatch the shopper is about to pick
      // (hover / touch / keyboard focus), so the swap on click is near-instant.
      const warm = (e) => {
        const sw = e.target.closest && e.target.closest('.fabric-selector__swatch');
        if (!sw) return;
        const input = sw.querySelector('input[type="radio"]');
        if (input) this.prefetchHero(input.dataset.fabricHandle);
      };
      this.addEventListener('mouseover', warm);
      this.addEventListener('touchstart', warm, { passive: true });
      this.addEventListener('focusin', warm);

      // Stain radios dispatch native bubbling change events — re-filter on any of them
      document.addEventListener('change', (e) => {
        const fieldset = e.target.closest('.product-form__input[data-handle]');
        if (fieldset && (this.stainHandles.includes(fieldset.dataset.handle) || this.sideHandles.includes(fieldset.dataset.handle))) {
          this.requestFilter();
        }
        // Any configurator pick (side, legs, width, …) can drive cfg-tagged gallery sets.
        if (e.target.closest && e.target.closest('[data-cfg]')) this.requestFilter();
      });

      this.initStickySync();

      // Guarantee a default selection on load, and sync the active box + ring + property from it.
      let selected = this.querySelector('input[type="radio"]:checked');
      if (!selected) {
        selected = this.querySelector('input[type="radio"]');
        if (selected) selected.checked = true;
      }
      if (selected) this.select(selected);

      // Order-swatches banner: dismissible, remembered per browser.
      const banner = this.querySelector('[data-fabric-order]');
      const closeBtn = this.querySelector('[data-fabric-order-close]');
      if (banner && closeBtn) {
        let dismissed = false;
        try { dismissed = localStorage.getItem('fabricOrderDismissed') === '1'; } catch (e) {}
        if (dismissed) banner.hidden = true;
        closeBtn.addEventListener('click', () => {
          banner.hidden = true;
          try { localStorage.setItem('fabricOrderDismissed', '1'); } catch (e) {}
        });
      }

      // Clamp the swatch grid to exactly two rows (measured, responsive).
      this._clamp = () => this.clampSwatchRows();
      const details = this.closest('details');
      if (details) details.addEventListener('toggle', () => { if (details.open) requestAnimationFrame(this._clamp); });
      window.addEventListener('resize', () => { clearTimeout(this._clampT); this._clampT = setTimeout(this._clamp, 150); });
      requestAnimationFrame(this._clamp);

      this.initFilters();
    }

    requestFilter() {
      cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => this.filterGallery());
    }

    get currentFabricHandle() {
      const checked = this.querySelector('input[type="radio"]:checked');
      return checked ? checked.dataset.fabricHandle : null;
    }
    // Side picker → 'laf' / 'raf' for the {side} axis in the image pattern.
    // Arm Side names the arm (Left Arm Facing = laf). Chaise Side / Corner Side name the
    // return, which sits opposite the arm, so Right = laf and Left = raf.
    get sideHandles() { return ['arm-side', 'chaise-side', 'corner-side']; }
    get currentSideSlug() {
      for (const handle of this.sideHandles) {
        const fieldset = document.querySelector(`.product-form__input[data-handle="${handle}"]`);
        if (!fieldset) continue;
        const el = fieldset.querySelector('input:checked') || fieldset.querySelector('select');
        if (!el) return null;
        const v = String(el.value || '').toLowerCase();
        const flip = handle !== 'arm-side';
        if (v.indexOf('left') === 0) return flip ? 'raf' : 'laf';
        if (v.indexOf('right') === 0) return flip ? 'laf' : 'raf';
        return this.slugify(el.value);
      }
      return null;
    }
    get currentStainSlug() {
      for (const handle of this.stainHandles) {
        const fieldset = document.querySelector(`.product-form__input[data-handle="${handle}"]`);
        if (!fieldset) continue;
        const checked = fieldset.querySelector('input:checked');
        if (checked) return this.slugify(checked.value);
        const select = fieldset.querySelector('select');
        if (select) return this.slugify(select.value);
      }
      return null;
    }

    slugify(value) {
      return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-/, '').replace(/-$/, '');
    }

    clampSwatchRows() {
      const grid = this.querySelector('.fabric-selector__grid');
      if (!grid) return;
      const items = Array.prototype.filter.call(grid.querySelectorAll('.fabric-selector__swatch'), function (el) { return !el.classList.contains('is-filtered-out'); });
      if (items.length <= 8) { grid.style.maxHeight = ''; return; }
      const delta = items[8].offsetTop - items[0].offsetTop;
      if (delta <= 0) return;
      const cs = getComputedStyle(grid);
      const gap = parseFloat(cs.rowGap) || 0;
      const h = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.paddingTop) || 0)
        + (delta - gap)
        + (parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
      grid.style.boxSizing = 'border-box';
      grid.style.maxHeight = Math.round(h) + 'px';
    }

    get headerOffset() {
      let bottom = 0;
      document.querySelectorAll('[id^="shopify-section"]').forEach((sec) => {
        if (!/header|announcement/i.test(sec.id)) return;
        [sec, ...sec.querySelectorAll(':scope > *, :scope > * > *')].forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.height > 0 && r.top < window.innerHeight / 2 && r.bottom > bottom) bottom = r.bottom;
        });
      });
      return bottom;
    }

    // ------- Fabric filters drawer -------
    initFilters() {
      const wrap = this.querySelector('[data-fabric-filters]');
      if (!wrap) return;
      const self = this;
      this.selColors = new Set();
      this.selMats = new Set();
      this.selFeats = new Set();

      const positionTop = () => { wrap.style.top = self.headerOffset + 'px'; };
      const openDrawer = () => { positionTop(); wrap.classList.add('is-open'); document.body.style.overflow = 'hidden'; };
      const closeDrawer = () => { wrap.classList.remove('is-open'); document.body.style.overflow = ''; };
      const openBtn = this.querySelector('[data-fabric-filters-open]');
      if (openBtn) openBtn.addEventListener('click', openDrawer);
      window.addEventListener('resize', () => { if (wrap.classList.contains('is-open')) positionTop(); });

      wrap.addEventListener('click', function (e) {
        if (e.target.closest('[data-fabric-filters-close], [data-fabric-filters-apply]')) {
          closeDrawer();
          return;
        }
        const chip = e.target.closest('.ff-chip');
        if (!chip) return;
        let set, val;
        if (chip.hasAttribute('data-color')) { set = self.selColors; val = chip.getAttribute('data-color'); }
        else if (chip.hasAttribute('data-mat')) { set = self.selMats; val = chip.getAttribute('data-mat'); }
        else return;
        if (set.has(val)) { set.delete(val); chip.classList.remove('is-sel'); }
        else { set.add(val); chip.classList.add('is-sel'); }
        self.applyFilter();
      });

      wrap.addEventListener('change', function (e) {
        const cb = e.target;
        if (!cb.hasAttribute || !cb.hasAttribute('data-feat')) return;
        if (cb.checked) self.selFeats.add(cb.getAttribute('data-feat'));
        else self.selFeats.delete(cb.getAttribute('data-feat'));
        self.applyFilter();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeDrawer();
      });

      this.applyFilter();
    }

    applyFilter() {
      const selC = this.selColors, selM = this.selMats, selF = this.selFeats;
      let visible = 0;
      this.querySelectorAll('.fabric-selector__swatch').forEach(function (sw) {
        const color = sw.getAttribute('data-color') || '';
        const mat = sw.getAttribute('data-material') || '';
        const feats = (sw.getAttribute('data-features') || '').split(',').filter(Boolean);
        let ok = true;
        if (selC.size && !selC.has(color)) ok = false;
        if (ok && selM.size && !selM.has(mat)) ok = false;
        if (ok && selF.size) { selF.forEach(function (x) { if (feats.indexOf(x) < 0) ok = false; }); }
        sw.classList.toggle('is-filtered-out', !ok);
        if (ok) visible++;
      });
      const showBtn = this.querySelector('[data-fabric-filters-apply]');
      if (showBtn) showBtn.textContent = 'Show ' + visible + (visible === 1 ? ' fabric' : ' fabrics');
      this.clampSwatchRows();
    }

    // Updates the active-selection box (name + type + price) and refreshes the gallery.
    select(input) {
      if (this.propertyInput) this.propertyInput.value = input.value;
      if (this.nameEl) this.nameEl.textContent = input.value;
      if (this.typeEl) this.typeEl.textContent = input.dataset.fabricType || '';
      if (this.priceEl) this.priceEl.textContent = input.dataset.fabricPrice || '';
      this.syncTierVariant(input);
      this.requestFilter();
      this.dispatchEvent(new CustomEvent('fabric:change', {
        bubbles: true,
        detail: { name: input.value, handle: input.dataset.fabricHandle }
      }));
    }

    // Fabric Tier is a hidden variant option (Standard / Tier 1 - Performance). The chosen
    // swatch decides it: pick the matching radio and let the theme's variant-selects handle
    // price, URL and sticky bar exactly as if the shopper had clicked it.
    syncTierVariant(input) {
      // On first paint this can run before the theme's variant-selects element is defined
      // (and therefore listening); wait for it, then sync from whatever is checked by then.
      if (!customElements.get('variant-selects')) {
        customElements.whenDefined('variant-selects').then(() => {
          const now = this.querySelector('input[type="radio"]:checked');
          if (now) this.syncTierVariant(now);
        });
        return;
      }
      const fieldset = document.querySelector('.product-form__input[data-handle="fabric-tier"]');
      if (!fieldset) return;
      const wantTier1 = /tier 1/i.test(input.dataset.fabricTier || '');
      const matches = (v) => (/tier 1/i.test(v || '')) === wantTier1;
      const select = fieldset.querySelector('select');
      if (select) {
        const opt = Array.prototype.find.call(select.options, (o) => matches(o.value));
        if (!opt || select.value === opt.value) return;
        select.value = opt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      const radio = Array.prototype.find.call(fieldset.querySelectorAll('input[type="radio"]'), (r) => matches(r.value));
      if (!radio || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // ------- Multi-axis (cfg) gallery sets -------
    // ALT convention: "Caption#cfg_side:left+fabric:pecan+legs:oiled-walnut"
    // An image declares constraints only for the axes it depends on. It shows when
    // EVERY declared constraint matches the current pick (exact slug or prefix,
    // so legs:oiled-walnut matches "Oiled Walnut — Single Wood Rail Leg").
    currentPicks() {
      const picks = {};
      document.querySelectorAll('[data-cfg]').forEach((fs) => {
        const el = fs.querySelector('input:checked');
        if (el) picks[fs.getAttribute('data-cfg')] = this.slugify(el.value);
      });
      const fabric = this.currentFabricHandle;
      if (fabric) picks.fabric = fabric;
      const stain = this.currentStainSlug;
      if (stain) picks.stain = stain;
      const side = this.currentSideSlug;
      if (side) picks.side = side;
      return picks;
    }

    cfgMatches(group, picks) {
      // group = "cfg_side:left+fabric:pecan" → constraints after the set prefix
      const tokens = group.slice(group.indexOf('_') + 1).split('+');
      return tokens.every((t) => {
        const i = t.indexOf(':');
        if (i < 1) return false;
        const axis = t.slice(0, i).trim();
        const want = t.slice(i + 1).trim();
        const have = picks[axis];
        return !!have && (have === want || have.indexOf(want + '-') === 0);
      });
    }

    filterCfg(slides, thumbs) {
      const picks = this.currentPicks();
      const eligible = slides.filter((s) => this.cfgMatches(s.dataset.group, picks));
      // Never blank the gallery: no eligible match → show every cfg image.
      const match = eligible.length
        ? (g) => this.cfgMatches(g, picks)
        : () => true;
      slides.forEach((s) => s.classList.toggle('is-active', match(s.dataset.group)));
      thumbs.forEach((t) => t.classList.toggle('is-active', match(t.dataset.group)));
      const slider = slides[0].closest('.product-images');
      if (slider && slider.flkty) slider.reInit();
      this.requestStickySync();
    }

    // ------- Dynamic hero image (Content > Files, no media limit) -------
    // A product with custom.configurator_image_pattern set (e.g.
    // "gaby_side-{side}_fabric-{fabric}_legs-{legs}_width-{width}.jpg") gets its
    // "#hero_dynamic"-tagged slide's image computed from the live picks and served
    // from /cdn/shop/files/. Missing files fail silently — the last image stays.
    heroNameFor(overrides) {
      const wrap = document.getElementById(`ProductInfo-${this.sectionId}`);
      const pattern = wrap && wrap.dataset.cfgImagePattern;
      if (!pattern) return null;
      const picks = Object.assign(this.currentPicks(), overrides || {});
      const name = pattern.replace(/\{(\w+)\}/g, (m, k) => picks[k] || m);
      return name.indexOf('{') === -1 ? name : null;
    }

    // "&v=<render_version>" — the uploader bumps custom.render_version whenever a product's renders are
    // replaced, so browsers and CDN edges fetch the new files instead of serving a cached older render.
    get renderVersionQuery() {
      const wrap = document.getElementById(`ProductInfo-${this.sectionId}`);
      const v = wrap && wrap.dataset.cfgImageVersion;
      return v ? `&v=${encodeURIComponent(v)}` : '';
    }

    prefetchHero(fabricHandle) {
      if (!fabricHandle) return;
      const name = this.heroNameFor({ fabric: fabricHandle });
      if (!name) return;
      this._warmed = this._warmed || {};
      if (this._warmed[name]) return;
      this._warmed[name] = true;
      const img = new Image();
      img.decoding = 'async';
      img.src = `/cdn/shop/files/${name}?width=1600${this.renderVersionQuery}`;
    }

    updateHero() {
      const wrap = document.getElementById(`ProductInfo-${this.sectionId}`);
      const pattern = wrap && wrap.dataset.cfgImagePattern;
      if (!pattern) return;
      const targets = [
        ...document.querySelectorAll(`[id^="Slide-${this.sectionId}-"][data-set-name="hero"] img`),
        ...document.querySelectorAll(`[id^="Thumb-${this.sectionId}-"][data-set-name="hero"] img`),
        ...document.querySelectorAll(`#product-image-${this.sectionId}--sticky img`)
      ];
      if (!targets.length) return;
      const picks = this.currentPicks();
      const name = pattern.replace(/\{(\w+)\}/g, (m, k) => picks[k] || m);
      if (name.indexOf('{') !== -1) return; // an axis is missing — leave the hero alone
      // Per-image staleness: the theme re-renders sticky content on variant changes,
      // replacing our swapped <img> with the original markup. Re-swap any target that
      // doesn't carry the current render name, even if the name itself hasn't changed.
      const stale = targets.filter((img) => img.dataset.cfgHero !== name);
      if (!stale.length) return;
      const src = `/cdn/shop/files/${name}`;
      const vq = this.renderVersionQuery;
      const probe = new Image();
      probe.onload = () => {
        const isFirstRun = !this._heroName;
        const nameChanged = this._heroName !== name;
        this._heroName = name;
        stale.forEach((img) => {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          let w = '?width=1600';
          if (img.closest(`[id^="Thumb-"]`)) w = '?width=200';
          else if (img.closest(`[id$="--sticky"]`)) w = '?width=240';
          img.src = src + w + vq;
          img.dataset.cfgHero = name;
          // Keep the zoom/lightbox on the same render (it reads the slide link when opened).
          const slideEl = img.closest('.product-images__slide');
          const link = slideEl && slideEl.querySelector('a.product-single__media-zoom, a[data-msrc]');
          if (link && img.closest('[data-set-name="hero"]')) {
            link.setAttribute('href', src + '?width=2880' + vq);
            link.setAttribute('data-msrc', src + '?width=1600' + vq);
            link.setAttribute('data-w', '2880');   // HookPic renders are 2880 × 2880
            link.setAttribute('data-h', '2880');
          }
        });
        // A visual pick was made: bring the carousel back to the hero slide so the
        // change is seen, even if a lifestyle image was active. Skip on initial load
        // and on re-applies after theme re-renders (same config, no reason to jump).
        if (!isFirstRun && nameChanged) {
          const heroSlide = document.querySelector(`[id^="Slide-${this.sectionId}-"][data-set-name="hero"]`);
          const slider = heroSlide && heroSlide.closest('.product-images');
          if (slider && slider.flkty && typeof slider.flkty.selectCell === 'function') {
            slider.flkty.selectCell(heroSlide, false, false);
          }
        }
      };
      probe.src = src + '?width=1600' + vq;
    }

    filterGallery() {
      this.updateHero();
      // cfg-tagged sets (multi-axis) take precedence when present.
      const cfgSlides = [...document.querySelectorAll(`[id^="Slide-${this.sectionId}-"]`)]
        .filter((s) => s.dataset.setName === 'cfg');
      if (cfgSlides.length) {
        const cfgThumbs = [...document.querySelectorAll(`[id^="Thumb-${this.sectionId}-"]`)]
          .filter((t) => t.dataset.setName === 'cfg');
        this.filterCfg(cfgSlides, cfgThumbs);
        return;
      }

      const fabric = this.currentFabricHandle;
      if (!fabric) return;

      const slides = [...document.querySelectorAll(`[id^="Slide-${this.sectionId}-"]`)]
        .filter((s) => s.dataset.setName === 'fabric');
      if (!slides.length) return;

      const thumbs = [...document.querySelectorAll(`[id^="Thumb-${this.sectionId}-"]`)]
        .filter((t) => t.dataset.setName === 'fabric');

      const stain = this.currentStainSlug;
      const exact = `fabric_${fabric}--${stain}`;
      const prefix = `fabric_${fabric}`;

      // Matching rule: exact fabric+stain combo → any image of this fabric → show all (never blank the gallery)
      let match;
      if (stain && slides.some((s) => s.dataset.group === exact)) {
        match = (g) => g === exact;
      } else if (slides.some((s) => s.dataset.group === prefix || s.dataset.group.startsWith(prefix + '--'))) {
        match = (g) => g === prefix || g.startsWith(prefix + '--');
      } else {
        match = () => true;
      }

      slides.forEach((s) => s.classList.toggle('is-active', match(s.dataset.group)));
      thumbs.forEach((t) => t.classList.toggle('is-active', match(t.dataset.group)));

      const slider = slides[0].closest('.product-images');
      if (slider && slider.flkty) slider.reInit();

      this.requestStickySync();
    }

    // ------- Sticky add-to-cart modal sync -------
    // The theme re-renders the modal's inner content on every variant change,
    // wiping anything we set. A MutationObserver re-applies our sync each time.

    initStickySync() {
      // Direct sticky bar (configurator products): the theme re-renders its content
      // on variant changes, wiping the dynamic hero thumb. Watch and re-apply.
      const bar = document.querySelector('product-add-to-cart-sticky[data-direct]');
      if (bar && bar.parentNode) {
        this._barObserver = new MutationObserver(() => {
          clearTimeout(this._barT);
          this._barT = setTimeout(() => this.requestFilter(), 80);
        });
        this._barObserver.observe(bar.parentNode, { childList: true, subtree: true });
      }

      this.stickyModal = document.getElementById(`StickyModal-${this.sectionId}`);
      if (!this.stickyModal) return;
      this._stickyObserver = new MutationObserver(() => {
        if (this._stickySyncing) return;
        this.requestStickySync();
      });
      this._stickyObserver.observe(this.stickyModal, { childList: true, subtree: true });
      this.requestStickySync();
    }

    requestStickySync() {
      clearTimeout(this._stickyTimer);
      this._stickyTimer = setTimeout(() => this.syncSticky(), 60);
    }

    syncSticky() {
      if (!this.stickyModal) return;
      this._stickySyncing = true;

      // 1. "Fabric: …" line under the modal title
      const info = this.stickyModal.querySelector('.product-add-to-cart-sticky--info');
      const name = this.propertyInput ? this.propertyInput.value : '';
      if (info && name) {
        let line = info.querySelector('.product-add-to-cart-sticky--fabric');
        if (!line) {
          line = document.createElement('div');
          line.className = 'product-add-to-cart-sticky--fabric';
          line.style.cssText = 'font-size: 12px; opacity: .7; margin: 2px 0 4px;';
          const title = info.querySelector('.product-add-to-cart-sticky--title');
          if (title) { title.after(line); } else { info.prepend(line); }
        }
        const text = `Fabric: ${name}`;
        if (line.textContent !== text) line.textContent = text;
      }

      // 2. Modal thumbnail = currently active silo image from the gallery.
      // The theme's lazysizes keeps re-applying data-srcset over anything we set,
      // so we take this img out of lazysizes' hands entirely and pin one URL.
      const container = document.getElementById(`product-image-${this.sectionId}--sticky`);
      const slide = [...document.querySelectorAll(`[id^="Slide-${this.sectionId}-"]`)]
        .find((s) => s.dataset.setName === 'fabric' && s.classList.contains('is-active'));
      const srcImg = slide ? slide.querySelector('img') : null;
      if (container && srcImg) {
        let img = container.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.setAttribute('width', '168');
          img.setAttribute('height', '168');
          container.replaceChildren(img);
        }
        img.classList.remove('lazyload', 'lazyloaded', 'lazyautosizes');
        ['data-srcset', 'data-sizes', 'data-src'].forEach((a) => img.removeAttribute(a));
        img.removeAttribute('srcset');
        try {
          const u = new URL(srcImg.currentSrc || srcImg.src, window.location.href);
          u.searchParams.set('width', '336');
          if (img.src !== u.href) img.src = u.href;
        } catch (e) { /* leave existing src */ }
        const alt = srcImg.getAttribute('alt') || '';
        if (img.getAttribute('alt') !== alt) img.setAttribute('alt', alt);
      }

      // Release the loop guard after pending mutation records have been delivered
      setTimeout(() => { this._stickySyncing = false; }, 0);
    }
  }
  customElements.define('fabric-selector', FabricSelector);
}
